/**
 * Local UI primitives for client-portal app.
 * Mirrors apps/admin/src/lib/ui.tsx.
 *
 * Note: client-portal session cookie is `mcs_access_token_client` (per spec).
 * The login URL stays the same; cookie naming is handled server-side.
 *
 * TODO: replace with imports from `@teivor/shared-ui` once parallel A.3
 * subagents export Button / Input / FormField / Switch / AppShell etc.
 */
import * as React from 'react';

import { cn } from './cn';

export const Button = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'secondary' | 'ghost' | 'link' | 'danger';
    fullWidth?: boolean;
    loading?: boolean;
  }
>(({ className, variant = 'primary', fullWidth, loading, disabled, children, ...props }, ref) => {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-[var(--radius-md)] h-9 px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)] disabled:pointer-events-none disabled:opacity-50';
  const variants: Record<string, string> = {
    primary: 'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]',
    secondary:
      'bg-[var(--color-surface-2)] text-[var(--color-fg)] border border-[var(--color-border)] hover:bg-[var(--color-subtle)]',
    ghost: 'bg-transparent text-[var(--color-fg)] hover:bg-[var(--color-subtle)]',
    link: 'bg-transparent text-[var(--color-accent)] underline-offset-4 hover:underline px-0',
    danger: 'bg-[var(--color-danger)] text-white hover:opacity-90',
  };
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], fullWidth && 'w-full', className)}
      {...props}
    >
      {loading ? <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" /> : null}
      {children}
    </button>
  );
});
Button.displayName = 'Button';

export const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(({ className, invalid, ...props }, ref) => (
  <input
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(
      'h-9 w-full rounded-[var(--radius-md)] border bg-[var(--color-surface)] px-3 text-sm text-[var(--color-fg)] placeholder:text-[var(--color-fg-muted)] outline-none transition-colors',
      'focus:ring-2 focus:ring-[var(--color-ring)] focus:ring-offset-1 focus:ring-offset-[var(--color-bg)]',
      invalid
        ? 'border-[var(--color-danger)] focus:ring-[var(--color-danger)]'
        : 'border-[var(--color-border)] focus:border-[var(--color-accent)]',
      className,
    )}
    {...props}
  />
));
Input.displayName = 'Input';

export const FormField: React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    label?: React.ReactNode;
    htmlFor?: string;
    required?: boolean;
    helperText?: React.ReactNode;
    error?: React.ReactNode;
  }
> = ({ className, label, htmlFor, required, helperText, error, children, ...props }) => (
  <div className={cn('flex flex-col gap-1.5', className)} {...props}>
    {label ? (
      <label htmlFor={htmlFor} className="text-xs font-medium text-[var(--color-fg-secondary)]">
        {label}
        {required ? <span className="ml-0.5 text-[var(--color-danger)]">*</span> : null}
      </label>
    ) : null}
    {children}
    {error ? (
      <p className="text-xs text-[var(--color-danger)]" role="alert">
        {error}
      </p>
    ) : helperText ? (
      <p className="text-xs text-[var(--color-fg-muted)]">{helperText}</p>
    ) : null}
  </div>
);

export const Switch = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label?: React.ReactNode }
>(({ className, label, id, ...props }, ref) => {
  const reactId = React.useId();
  const inputId = id || reactId;
  return (
    <label htmlFor={inputId} className={cn('inline-flex items-center gap-2 cursor-pointer text-sm', className)}>
      <input ref={ref} id={inputId} type="checkbox" className="h-4 w-4 rounded border-[var(--color-border)]" {...props} />
      {label ? <span>{label}</span> : null}
    </label>
  );
});
Switch.displayName = 'Switch';

export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>((props, ref) => {
  const [show, setShow] = React.useState(false);
  return (
    <div className="relative">
      <Input ref={ref} type={show ? 'text' : 'password'} {...props} className="pr-16" />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-0.5 text-xs text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';

export interface CodeInputProps {
  length?: number;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
}

export const CodeInput: React.FC<CodeInputProps> = ({
  length = 6,
  value,
  onChange,
  invalid,
  autoFocus,
  'aria-label': ariaLabel = 'Verification code',
}) => {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);
  const chars = value.padEnd(length, ' ').slice(0, length).split('');
  React.useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);
  const setAt = (i: number, ch: string) => {
    const arr = chars.map((c) => (c === ' ' ? '' : c));
    arr[i] = ch;
    onChange(arr.join('').slice(0, length));
  };
  return (
    <div role="group" aria-label={ariaLabel} className="flex w-full max-w-full gap-2 sm:inline-flex sm:w-auto">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          aria-label={`Digit ${i + 1}`}
          aria-invalid={invalid || undefined}
          value={chars[i] === ' ' ? '' : chars[i]}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(-1);
            if (v) {
              setAt(i, v);
              if (i < length - 1) refs.current[i + 1]?.focus();
            } else {
              setAt(i, '');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !chars[i].trim() && i > 0) refs.current[i - 1]?.focus();
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
            if (pasted) {
              e.preventDefault();
              onChange(pasted);
              refs.current[Math.min(pasted.length, length - 1)]?.focus();
            }
          }}
          className={cn(
            'h-12 w-10 min-w-0 max-w-10 flex-1 rounded-[var(--radius-md)] border bg-[var(--color-surface)] text-center text-lg font-semibold outline-none transition-colors sm:flex-none',
            'focus:ring-2 focus:ring-[var(--color-ring)]',
            invalid
              ? 'border-[var(--color-danger)] focus:ring-[var(--color-danger)]'
              : 'border-[var(--color-border)] focus:border-[var(--color-accent)]',
          )}
        />
      ))}
    </div>
  );
};

export function PasswordStrengthMeter({ value }: { value: string }) {
  const score = React.useMemo(() => {
    let s = 0;
    if (value.length >= 8) s++;
    if (value.length >= 12) s++;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) s++;
    if (/\d/.test(value)) s++;
    if (/[^A-Za-z0-9]/.test(value)) s++;
    return Math.min(s, 4);
  }, [value]);
  const colors = [
    'bg-[var(--color-border)]',
    'bg-[var(--color-danger)]',
    'bg-[var(--color-warning,#d97706)]',
    'bg-[var(--color-accent)]',
    'bg-[var(--color-success,#16a34a)]',
  ];
  return (
    <div className="flex gap-1" aria-label={`Password strength: ${score} of 4`}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={cn('h-1 flex-1 rounded-full', i < score ? colors[score] : 'bg-[var(--color-border)]')} />
      ))}
    </div>
  );
}
